import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Chip, Button, Paper, Alert, Snackbar, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, List, ListItem, ListItemText,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BlockIcon from '@mui/icons-material/Block';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import useIsMobile from '../hooks/useIsMobile';
import ComposerMailDialog from './ComposerMailDialog';
import JoindreFormulaireDialog from './JoindreFormulaireDialog';
import ReponsesFormulaire from './ReponsesFormulaire';
import {
  Definition, EntreeJournal, Instance, InstanceCreee, InstancesParChemin, Reponses, StatutInstance,
  LIBELLES_STATUTS, LIBELLES_EVENEMENTS,
  messageErreurs, parseJson,
} from '../services/formulairesServices';
import { useApiFormulaires } from '../formulaires/ApiFormulairesContext';

// Panneau « Formulaires » d'un dossier (lot 5), inséré dans InfoFormulaire sous « Détails du formulaire » : liste des
// formulaires joints (GET /formulaires/dossiers/par-chemin/:chemin, id DESC) avec statut, dates, actions (Envoyer /
// Relancer via le composeur, Copier le lien, Rouvrir, Annuler, Journal, Supprimer) et réponses repliables ; puis, pour
// un dossier créé manuellement (source = 'admin_manuel'), « Joindre un formulaire ».
// Appels par ApiFormulairesContext (admin par défaut, Legrand dans l'admin Legrand). lectureSeule (compte Legrand
// non superadmin) : statut, dates, destinataire, réponses et journal seulement, aucune action ni lien.
// Rend null (aucune trace dans le panneau) tant que la liste n'est pas chargée, si l'appel échoue (503 tables absentes,
// 404, réseau) ou si le dossier n'est pas manuel et n'a aucun formulaire : le panneau des dossiers app reste identique.
// L'admin n'écrit jamais `reponses` : il affiche ce que le backend client a calculé.

interface FormulairesDossierProps {
  chemin: string;
  lectureSeule?: boolean;
}

// Instance telle qu'affichée : colonnes JSON passées par parseJson (le backend les parse déjà ; défense si un driver
// les renvoyait en chaîne), `definition` pouvant alors être null.
type InstanceVue = Omit<Instance, 'definition' | 'reponses' | 'journal'> & {
  definition: Definition | null;
  reponses: Reponses | null;
  journal: EntreeJournal[];
};

interface EtatPanneau {
  dossier: InstancesParChemin['dossier'];
  instances: InstanceVue[];
}

interface Toast {
  msg: string;
  sev: 'success' | 'error' | 'warning' | 'info';
}

interface Confirmation {
  titre: string;
  texte: string;
  libelleBouton: string;
  couleur: 'primary' | 'error' | 'warning';
  action: () => void;
}

// Contenu du composeur (ComposerMailDialog) préparé par GET /apercu-mail
interface Composeur {
  idInstance: number;
  titre: string;
  destinataire: string;
  sujet: string;
  corpsHtml: string;
}

const COULEURS_STATUT: Record<StatutInstance, 'default' | 'primary' | 'warning' | 'success' | 'error'> = {
  cree: 'default',      // gris
  envoye: 'primary',    // bleu
  en_cours: 'warning',  // orange
  soumis: 'success',    // vert
  annule: 'error',      // rouge
};

const AVERTISSEMENT_ARCHIVE = 'Dossier archivé : le lien répond 410 tant qu\'il est archivé.';

const normaliserInstance = (i: Instance): InstanceVue => ({
  ...i,
  definition: parseJson<Definition>(i.definition),
  reponses: parseJson<Reponses>(i.reponses),
  journal: parseJson<EntreeJournal[]>(i.journal) || [],
  fichiers_urls: i.fichiers_urls || {},
});

const versEtat = (r: InstancesParChemin): EtatPanneau => ({
  dossier: r.dossier,
  instances: (Array.isArray(r.instances) ? r.instances : []).map(normaliserInstance),
});

const pad2 = (n: number): string => (n < 10 ? '0' : '') + n;

// ISO (ou Date sérialisée) -> jj/mm/aaaa hh:mm en heure locale ; la chaîne brute si illisible
const formaterDateHeure = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// Nombre de questions obligatoires actuellement visibles (hors info), dénominateur de « n/m obligatoires »
const nbObligatoiresVisibles = (def: Definition | null, visibles: string[]): number => {
  if (!def || !Array.isArray(def.sections)) return 0;
  let n = 0;
  def.sections.forEach((s) => {
    s.questions.forEach((q) => { if (q.type !== 'info' && q.obligatoire && visibles.indexOf(q.id) >= 0) n += 1; });
  });
  return n;
};

// Libellé du Chip : statut, complété pour en_cours de « n/m obligatoires » (n = manquantes, m = obligatoires visibles)
const libelleChip = (i: InstanceVue): string => {
  const base = LIBELLES_STATUTS[i.statut] || i.statut;
  if (i.statut !== 'en_cours' || !i.reponses) return base;
  const manquantes = Array.isArray(i.reponses.manquantes) ? i.reponses.manquantes.length : 0;
  const total = nbObligatoiresVisibles(i.definition, Array.isArray(i.reponses.visibles) ? i.reponses.visibles : []);
  if (manquantes === 0) return `${base} · ${total}/${total} obligatoires renseignées`;
  return `${base} · ${manquantes} manquante${manquantes > 1 ? 's' : ''} sur ${total} obligatoires`;
};

// « créé le … · envoyé ×n le … · dernière sauvegarde … · soumis le … »
const ligneDates = (i: InstanceVue): string => {
  const parts: string[] = [`créé le ${formaterDateHeure(i.date_creation)}`];
  if (i.nb_envois > 0 && i.date_envoi) parts.push(`envoyé ×${i.nb_envois} le ${formaterDateHeure(i.date_envoi)}`);
  if (i.date_sauvegarde) parts.push(`dernière sauvegarde ${formaterDateHeure(i.date_sauvegarde)}`);
  if (i.date_soumission) parts.push(`soumis le ${formaterDateHeure(i.date_soumission)}`);
  return parts.join(' · ');
};

const FormulairesDossier: React.FC<FormulairesDossierProps> = ({ chemin, lectureSeule = false }) => {
  const isMobile = useIsMobile();
  const api = useApiFormulaires();
  const [etat, setEtat] = useState<EtatPanneau | null>(null);
  const [erreur, setErreur] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [journalDe, setJournalDe] = useState<InstanceVue | null>(null);
  const [chargementComposeur, setChargementComposeur] = useState<number | null>(null); // id en cours de préparation
  const [composeur, setComposeur] = useState<Composeur | null>(null);
  const [actionEnCours, setActionEnCours] = useState<number | null>(null); // id en cours de rouvrir/annuler/supprimer
  const [joindreOuvert, setJoindreOuvert] = useState(false);

  // Chemin courant, pour ignorer un rechargement résolu après un changement de dossier
  const cheminRef = useRef(chemin);
  cheminRef.current = chemin;

  useEffect(() => {
    let actif = true;
    setEtat(null);
    setErreur(false);
    setToast(null);
    setComposeur(null);
    setJoindreOuvert(false);
    setJournalDe(null);
    setConfirmation(null);
    if (!chemin) { setErreur(true); return undefined; }
    api.instancesParChemin(chemin)
      .then((r) => { if (actif) setEtat(versEtat(r)); })
      .catch((e) => {
        if (!actif) return;
        // 503 (tables absentes), 404, réseau : le panneau du dossier reste tel qu'avant la feature
        console.error('Erreur chargement des formulaires du dossier', e);
        setErreur(true);
      });
    return () => { actif = false; };
  }, [chemin, api]);

  // Rechargement après une action (sans passer par null : pas de clignotement) ; l'ancien état reste affiché en cas d'échec
  const recharger = async () => {
    const c = cheminRef.current;
    try {
      const r = await api.instancesParChemin(c);
      if (cheminRef.current === c) { setEtat(versEtat(r)); setErreur(false); }
    } catch (e) {
      console.error('Erreur rechargement des formulaires du dossier', e);
      setToast({ msg: messageErreurs(e, 'Rechargement des formulaires impossible'), sev: 'error' });
    }
  };

  // GET /apercu-mail puis ouverture du composeur ; en échec (502 template désactivé, 409 statut, 503, réseau) le
  // composeur n'est pas ouvert et l'instance est inchangée. `apresCreation` : message du cas « Joindre » (le
  // formulaire existe bien, statut créé, envoyable depuis ce panneau).
  const preparerComposeur = async (id: number, nomModele: string, relance: boolean, apresCreation: boolean) => {
    setChargementComposeur(id);
    const c = cheminRef.current; // dossier au moment du clic : réponse ignorée si l'admin a changé de dossier entre-temps
    try {
      const ap = await api.apercuMailInstance(id, relance);
      if (cheminRef.current !== c) return;
      setComposeur({
        idInstance: id,
        titre: relance ? `Relancer le client — ${nomModele}` : `Envoyer le formulaire au client — ${nomModele}`,
        destinataire: ap.destinataire,
        sujet: ap.sujet,
        corpsHtml: ap.corps_html,
      });
    } catch (e) {
      console.error('Erreur préparation du mail', e);
      if (cheminRef.current !== c) return;
      if (apresCreation) {
        setToast({
          msg: `Formulaire joint (statut créé) mais le mail n'a pas pu être préparé : ${messageErreurs(e, 'erreur inconnue')}. Envoyez-le depuis ce panneau`,
          sev: 'warning',
        });
      } else {
        setToast({ msg: messageErreurs(e, 'Impossible de préparer le mail'), sev: 'error' });
      }
    } finally {
      setChargementComposeur(null);
    }
  };

  // Après « Créer et composer le mail » : liste rechargée, puis composeur (premier envoi)
  const handleCree = async (instance: InstanceCreee) => {
    setJoindreOuvert(false);
    await recharger();
    await preparerComposeur(instance.id, instance.nom_modele, false, true);
  };

  // Rouvrir / Annuler / Supprimer : action, toast, rechargement
  const executer = async (id: number, action: () => Promise<unknown>, succes: string, defaut: string) => {
    setActionEnCours(id);
    try {
      await action();
      setToast({ msg: succes, sev: 'success' });
      await recharger();
    } catch (e) {
      console.error(defaut, e);
      setToast({ msg: messageErreurs(e, defaut), sev: 'error' });
    } finally {
      setActionEnCours(null);
    }
  };

  const copierLien = async (url: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setToast({ msg: 'Lien copié', sev: 'success' });
        return;
      }
    } catch (e) {
      console.error('Presse-papiers indisponible', e);
    }
    window.prompt('Copiez le lien du formulaire :', url);
  };

  const confirmerRouvrir = (i: InstanceVue) => setConfirmation({
    titre: 'Rouvrir le formulaire',
    texte: `Rouvrir « ${i.nom_modele} » ? Le client pourra à nouveau modifier ses réponses avec le même lien.`,
    libelleBouton: 'Rouvrir',
    couleur: 'primary',
    action: () => executer(i.id, () => api.rouvrirInstance(i.id), 'Formulaire rouvert', 'Réouverture impossible'),
  });

  const confirmerAnnuler = (i: InstanceVue) => setConfirmation({
    titre: 'Annuler le formulaire',
    texte: `Annuler « ${i.nom_modele} » ? Le lien envoyé au client ne fonctionnera plus. Pour renvoyer un formulaire, joignez-en un nouveau.`,
    libelleBouton: 'Annuler le formulaire',
    couleur: 'error',
    action: () => executer(i.id, () => api.annulerInstance(i.id), 'Formulaire annulé', 'Annulation impossible'),
  });

  const confirmerSupprimer = (i: InstanceVue) => setConfirmation({
    titre: 'Supprimer le formulaire',
    texte: `Supprimer « ${i.nom_modele} » (jamais envoyé ni rempli) ?`,
    libelleBouton: 'Supprimer',
    couleur: 'error',
    action: () => executer(i.id, () => api.supprimerInstance(i.id), 'Formulaire supprimé', 'Suppression impossible'),
  });

  const lancerConfirmation = () => {
    if (!confirmation) return;
    const { action } = confirmation;
    setConfirmation(null);
    action();
  };

  // Envoi depuis le composeur : le rejet (400, 409, 502) est attrapé par ComposerMailDialog ; la liste n'est rechargée
  // que si le mail est parti (envoye === true)
  const idComposeur = composeur ? composeur.idInstance : null;
  const onEnvoyer = async (p: { email: string; sujet: string; corps_html: string }) => {
    if (idComposeur === null) return { envoye: false, erreur: 'Aucun formulaire sélectionné' };
    const r = await api.envoyerInstance(idComposeur, p);
    if (r.mail && r.mail.envoye) recharger();
    return r.mail;
  };

  if (erreur || !etat) return null;
  const { dossier, instances } = etat;
  const manuel = dossier.source === 'admin_manuel';
  if (!manuel && instances.length === 0) return null;
  const archive = !!dossier.archived;

  return (
    <Box sx={{ p: 1 }}>
      {instances.length === 0 ? (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Formulaires : <Box component="span" sx={{ fontWeight: 'normal', fontStyle: 'italic', color: 'text.secondary' }}>aucun</Box>
        </Typography>
      ) : (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Formulaires:
        </Typography>
      )}

      {archive && <Alert severity="warning" sx={{ mb: 1 }}>{AVERTISSEMENT_ARCHIVE}</Alert>}

      {instances.map((i) => {
        const occupe = actionEnCours === i.id || chargementComposeur === i.id;
        return (
          <Paper key={i.id} variant="outlined" sx={{ p: 1, mb: 1, opacity: i.statut === 'annule' ? 0.7 : 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography component="span" sx={{ fontWeight: 500 }}>{i.nom_modele}</Typography>
              {i.version_modele !== null && (
                <Typography component="span" variant="caption" color="text.secondary">v{i.version_modele}</Typography>
              )}
              <Tooltip
                title={i.statut === 'en_cours' && i.reponses
                  ? `${Array.isArray(i.reponses.manquantes) ? i.reponses.manquantes.length : 0} réponse(s) obligatoire(s) manquante(s) sur les questions obligatoires visibles`
                  : ''}
              >
                <Chip label={libelleChip(i)} size="small" color={COULEURS_STATUT[i.statut] || 'default'} />
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {ligneDates(i)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Destinataire : {i.email_destinataire}
            </Typography>

            {/* Actions selon le statut ; en lecture seule, seulement le journal */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {!lectureSeule && i.statut === 'cree' && (
                <Button
                  size="small" variant="contained" disabled={occupe}
                  startIcon={chargementComposeur === i.id ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                  onClick={() => preparerComposeur(i.id, i.nom_modele, false, false)}
                  sx={{ textTransform: 'none' }}
                >
                  Envoyer
                </Button>
              )}
              {!lectureSeule && (i.statut === 'envoye' || i.statut === 'en_cours') && (
                <Button
                  size="small" variant="outlined" disabled={occupe}
                  startIcon={chargementComposeur === i.id ? <CircularProgress size={14} /> : <ReplayIcon />}
                  onClick={() => preparerComposeur(i.id, i.nom_modele, true, false)}
                  sx={{ textTransform: 'none' }}
                >
                  Relancer
                </Button>
              )}
              {!lectureSeule && i.url && (
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => { if (i.url) copierLien(i.url); }} sx={{ textTransform: 'none' }}>
                  Copier le lien
                </Button>
              )}
              {!lectureSeule && i.statut === 'soumis' && (
                <Button size="small" startIcon={<LockOpenIcon />} disabled={occupe} onClick={() => confirmerRouvrir(i)} sx={{ textTransform: 'none' }}>
                  Rouvrir
                </Button>
              )}
              {!lectureSeule && i.statut !== 'annule' && (
                <Button size="small" color="warning" startIcon={<BlockIcon />} disabled={occupe} onClick={() => confirmerAnnuler(i)} sx={{ textTransform: 'none' }}>
                  Annuler
                </Button>
              )}
              <Button size="small" startIcon={<HistoryIcon />} onClick={() => setJournalDe(i)} sx={{ textTransform: 'none' }}>
                Journal
              </Button>
              {!lectureSeule && i.statut === 'cree' && i.reponses === null && (
                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={occupe} onClick={() => confirmerSupprimer(i)} sx={{ textTransform: 'none' }}>
                  Supprimer
                </Button>
              )}
            </Box>

            <Box sx={{ mt: 0.5 }}>
              <ReponsesFormulaire definition={i.definition} reponses={i.reponses} statut={i.statut} fichiersUrls={i.fichiers_urls} />
            </Box>
          </Paper>
        );
      })}

      {manuel && !lectureSeule && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setJoindreOuvert(true)}
          sx={{ textTransform: 'none', mt: instances.length ? 0 : 0.5 }}
        >
          {instances.length > 0 ? 'Joindre un autre formulaire' : 'Joindre un formulaire'}
        </Button>
      )}

      <JoindreFormulaireDialog
        open={joindreOuvert}
        chemin={chemin}
        emailPropose={dossier.email || ''}
        onClose={() => setJoindreOuvert(false)}
        onCree={handleCree}
      />

      <ComposerMailDialog
        open={!!composeur}
        titre={composeur ? composeur.titre : ''}
        destinataire={composeur ? composeur.destinataire : ''}
        sujet={composeur ? composeur.sujet : ''}
        corpsHtml={composeur ? composeur.corpsHtml : ''}
        avertissement={archive ? AVERTISSEMENT_ARCHIVE : undefined}
        onEnvoyer={onEnvoyer}
        onClose={() => setComposeur(null)}
      />

      {/* Journal d'une instance : { d, e, x } */}
      <Dialog open={!!journalDe} onClose={() => setJournalDe(null)} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>Journal — {journalDe ? journalDe.nom_modele : ''}</DialogTitle>
        <DialogContent dividers>
          {journalDe && journalDe.journal.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Journal vide</Typography>
          )}
          <List dense disablePadding>
            {(journalDe ? journalDe.journal : []).map((entree, k) => (
              <ListItem key={`${entree.d}-${k}`} disableGutters sx={{ color: entree.e === 'envoi_non_confirme' ? 'warning.main' : 'inherit' }}>
                <ListItemText
                  primary={`${formaterDateHeure(entree.d)} — ${LIBELLES_EVENEMENTS[entree.e] || entree.e}`}
                  secondary={entree.x || undefined}
                  primaryTypographyProps={{ variant: 'body2', sx: { color: 'inherit' } }}
                  secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJournalDe(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation (Rouvrir / Annuler / Supprimer) */}
      <Dialog open={!!confirmation} onClose={() => setConfirmation(null)} fullScreen={isMobile}>
        <DialogTitle>{confirmation ? confirmation.titre : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmation ? confirmation.texte : ''}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation(null)}>Retour</Button>
          <Button onClick={lancerConfirmation} color={confirmation ? confirmation.couleur : 'primary'} autoFocus>
            {confirmation ? confirmation.libelleBouton : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={8000}
        onClose={(_e, raison) => { if (raison !== 'clickaway') setToast(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default FormulairesDossier;
